import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartNoAxesColumn,
  ChartScatter,
  Ellipsis,
  FileText,
  Grid3x3,
  Orbit,
  PanelLeft,
  RefreshCw,
  Scan,
  Search,
  SlidersHorizontal,
  Sparkles,
  UserRound,
  X,
} from 'lucide-react';
import { DashboardLayout } from '../../../components/dashboard';
import { Button, Dialog, DialogBody, DialogHeader, EmptyState, IconButton, Menu, Pill, SegmentedControl, Spinner } from '../../../components/ui';
import { useDebounce } from '../../../hooks/useDebounce';
import { asFlagOn, asList, asNumber, asOneOf, useUrlState, useUrlWriter } from '../../../hooks/useUrlState';
import type { UrlCodec } from '../../../hooks/useUrlState';
import { AskPanel, AskPanelHandle } from '../components/AskPanel';
import { BriefPanel } from '../components/BriefPanel';
import { analyzeNetwork } from '../graphAnalytics';
import { assessInfluence } from '../intelligenceApi';
import type { AssessInfluenceRequest, InfluenceLead } from '../types';
import { useIntelligenceStream } from '../useIntelligenceStream';
import { AssessStream } from './AssessPanel';
import { BriefingRail } from './BriefingRail';
import { ComparePanel, MAX_COMPARE } from './ComparePanel';
import { EntitySearch, FiltersPanel } from './Controls';
import { Dossier } from './Dossier';
import {
  buildInfluenceContext,
  crossDomainInfluencers,
  findGaps,
  InsightBundle,
  influenceSuggestions,
  leveragePoints,
  networkPercentile,
  summarizeAreas,
} from './influenceInsights';
import {
  activeFilterCount,
  applyFilters,
  DEFAULT_FILTERS,
  InfluenceEntity,
  InfluenceFilters,
  matchesQuery,
  profilePath,
  UNASSESSED_REGION,
} from './influenceModel';
import { LeverageLens } from './LeverageLens';
import { MatrixLens, MatrixSort } from './MatrixLens';
import { PowerMap, PowerMapHandle, RegionMeta } from './PowerMap';
import { InfluenceStyles, areaColor, useMediaQuery, usePrefersReducedMotion } from './ui';
import { useInfluenceData } from './useInfluenceData';

export type Lens = 'map' | 'matrix' | 'leverage';
const LENSES: Lens[] = ['map', 'matrix', 'leverage'];
const LENS_LABEL: Record<Lens, string> = { map: 'Power map', matrix: 'Matrix', leverage: 'Leverage' };
const LENS_KEY = 'staketory.influence.lens';

type SheetTab = 'briefing' | 'dossier' | 'compare' | 'ask';
/** The panel beside the lens on a desktop; the phone shows these as sheets instead */
type RightMode = 'dossier' | 'compare' | 'ask';

/* One codec per parameter, made once: the URL state reads them on every render */
const LENS = asOneOf(LENSES);
const ENTITY_TYPE = asOneOf(['all', 'stakeholder', 'organization'] as const);
const MIN_SCORE = asNumber(0, 95);
const ID_LIST = asList<string>();
const SORT_DIR = asOneOf(['asc', 'desc'] as const);
const PANEL = asOneOf<RightMode | ''>(['dossier', 'compare', 'ask']);

/*
 * The rail and the unassessed entities are there to begin with, so their
 * parameter has to be able to say "off". `asFlag` can only say "on".
 */
/* No `areas` at all means every area; picking none of them is its own answer */
const NO_AREAS = 'none';
const AREA_IDS: UrlCodec<string[] | null> = {
  read: (raw) => (raw === NO_AREAS ? [] : ID_LIST.read(raw)),
  write: (value) => (value === null ? null : value.length ? ID_LIST.write(value) : NO_AREAS),
};

const DEFAULT_SORT: MatrixSort = { key: 'average', dir: 'desc' };
/** Shared empty list, so "nothing compared" keeps one identity across renders */
const NO_IDS: string[] = [];

/*
 * Full-bleed workspace: cancels the dashboard's main padding and fills the space
 * between the header and the phone tab bar.
 */
const WORKSPACE_CLASS =
  'relative -mx-4 -mt-4 flex h-[calc(100dvh_-_57px_-_env(safe-area-inset-top,0px)_-_var(--app-tabbar-h))] flex-col overflow-hidden bg-white font-ui text-ink antialiased sm:-mx-6 sm:-mt-6 lg:h-[calc(100dvh_-_65px_-_env(safe-area-inset-top,0px))]';
const WORKSPACE_STYLE: React.CSSProperties = { marginBottom: 'calc(-1 * (var(--app-tabbar-h, 0px) + 1.5rem))' };
const FLOATING = 'bg-white/95 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06] backdrop-blur-sm';

function readLens(): Lens {
  try {
    const v = localStorage.getItem(LENS_KEY);
    return v === 'matrix' || v === 'leverage' ? v : 'map';
  } catch {
    return 'map';
  }
}

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && !!target.closest('input, textarea, select, [contenteditable="true"]');

export const InfluenceWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const { status, model, error, refreshing, reload } = useInfluenceData();
  const reducedMotion = usePrefersReducedMotion();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  /* ---------------- View state, kept in the address bar ---------------- */
  /*
   * The lens, the filters and who is being read are in the query string, so a
   * link shows someone else the same view. The lens is also remembered per
   * browser and that memory is only the fallback: a link always wins, but a
   * visit that arrives without one opens where this reader left off.
   */
  const [storedLens] = useState(readLens);
  const [lens] = useUrlState<Lens>('lens', storedLens, LENS);
  const [filterType] = useUrlState('type', DEFAULT_FILTERS.type, ENTITY_TYPE);
  const [areaIds] = useUrlState('areas', DEFAULT_FILTERS.areaIds, AREA_IDS);
  const [minScore] = useUrlState('minScore', DEFAULT_FILTERS.minScore, MIN_SCORE);
  const [showUnassessed] = useUrlState('unassessed', DEFAULT_FILTERS.showUnassessed, asFlagOn);
  const [query] = useUrlState('q', '');
  const [selParam] = useUrlState('sel', '');
  const [compareIds] = useUrlState('compare', NO_IDS, ID_LIST);
  const [panel] = useUrlState<RightMode | ''>('panel', '', PANEL);
  const [railOpen] = useUrlState('rail', true, asFlagOn);
  const [areaParam] = useUrlState('area', '');
  const [sortKey] = useUrlState('sortBy', DEFAULT_SORT.key);
  const [sortDir] = useUrlState('sortDir', DEFAULT_SORT.dir, SORT_DIR);

  const selectedId = selParam || null;
  const focusAreaId = areaParam || null;
  const rightMode: RightMode | null = panel || null;
  const filters = useMemo<InfluenceFilters>(
    () => ({ type: filterType, areaIds, minScore, showUnassessed }),
    [filterType, areaIds, minScore, showUnassessed],
  );
  const matrixSort = useMemo<MatrixSort>(() => ({ key: sortKey, dir: sortDir }), [sortKey, sortDir]);

  /*
   * Every write goes through here. Choosing a filter or an area moves several
   * parameters at once — an area focuses the map and sorts the matrix by it —
   * and those belong in the URL as one change, not as a handful that happen to
   * arrive together.
   */
  const write = useUrlWriter();
  const setView = useCallback(
    (
      next: Partial<{
        lens: Lens;
        q: string;
        sel: string | null;
        compare: string[];
        panel: RightMode | null;
        rail: boolean;
        area: string | null;
        sort: MatrixSort;
        filters: InfluenceFilters;
      }>,
    ) => {
      const changes: Record<string, string | null> = {};
      if (next.lens !== undefined) changes.lens = next.lens === storedLens ? null : next.lens;
      if (next.q !== undefined) changes.q = next.q || null;
      if (next.sel !== undefined) changes.sel = next.sel || null;
      if (next.compare !== undefined) changes.compare = ID_LIST.write(next.compare);
      if (next.panel !== undefined) changes.panel = next.panel || null;
      if (next.rail !== undefined) changes.rail = asFlagOn.write(next.rail);
      if (next.area !== undefined) changes.area = next.area || null;
      if (next.sort !== undefined) {
        changes.sortBy = next.sort.key === DEFAULT_SORT.key ? null : next.sort.key;
        changes.sortDir = next.sort.dir === DEFAULT_SORT.dir ? null : next.sort.dir;
      }
      if (next.filters !== undefined) {
        changes.type = next.filters.type === DEFAULT_FILTERS.type ? null : next.filters.type;
        changes.areas = AREA_IDS.write(next.filters.areaIds);
        changes.minScore = next.filters.minScore > DEFAULT_FILTERS.minScore ? String(next.filters.minScore) : null;
        changes.unassessed = asFlagOn.write(next.filters.showUnassessed);
      }
      write(changes);
    },
    [write, storedLens],
  );

  const setLens = useCallback((next: Lens) => setView({ lens: next }), [setView]);
  const setFilters = useCallback((next: InfluenceFilters) => setView({ filters: next }), [setView]);
  const setMatrixSort = useCallback((next: MatrixSort) => setView({ sort: next }), [setView]);

  // Remembered however the lens changed, including by link or back button
  useEffect(() => {
    try {
      localStorage.setItem(LENS_KEY, lens);
    } catch {
      // storage unavailable
    }
  }, [lens]);

  // Only the lenses that have been looked at are mounted
  const [visited, setVisited] = useState<Lens[]>(() => [lens]);
  useEffect(() => {
    setVisited((v) => (v.includes(lens) ? v : [...v, lens]));
  }, [lens]);

  // The box answers every keystroke; the URL catches up once typing stops
  const [search, setSearch] = useState(query);
  const settledSearch = useDebounce(search, 300);
  useEffect(() => {
    if (settledSearch !== query) setView({ q: settledSearch });
  }, [settledSearch, query, setView]);

  const [lensHover, setLensHover] = useState<string | null>(null);
  const [panelHover, setPanelHover] = useState<string | null>(null);
  const [highlightIds, setHighlightIds] = useState<string[]>([]);
  const [sheet, setSheet] = useState<SheetTab | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState(false);
  const [barSearch, setBarSearch] = useState(false);
  const [rootWidth, setRootWidth] = useState(1280);
  const rootRef = useRef<HTMLDivElement>(null);

  const mapRef = useRef<PowerMapHandle>(null);
  const askRef = useRef<AskPanelHandle>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const sheetTitleId = useId();

  /* ---------------- AI assessment stream (kept here so switching entity doesn't abort it) ---------------- */
  const assessStream = useIntelligenceStream<AssessInfluenceRequest, { lead: InfluenceLead }>(assessInfluence);
  const [assessSubject, setAssessSubject] = useState<string | null>(null);
  const assess: AssessStream = {
    status: assessStream.status,
    text: assessStream.text,
    progress: assessStream.progress,
    result: assessStream.result,
    error: assessStream.error,
    entityIds: assessStream.entityIds,
    subjectId: assessSubject,
    start: (entity, focus) => {
      setAssessSubject(entity.id);
      assessStream.start({ entityId: entity.id, entityType: entity.type, focus });
    },
    stop: assessStream.stop,
  };

  /* ---------------- Analytics ---------------- */
  const analysis = useMemo(
    () =>
      model
        ? analyzeNetwork(
            model.entities.map((e) => e.id),
            model.links.map((l) => ({ source: l.source, target: l.target, weight: l.weight })),
          )
        : null,
    [model],
  );
  const view = useMemo(() => (model ? applyFilters(model, filters) : null), [model, filters]);
  const bundle: InsightBundle | null = useMemo(
    () =>
      view
        ? {
            areas: summarizeAreas(view.areas, view.entities, filters.minScore),
            cross: crossDomainInfluencers(view.assessed, view.areaIds),
            gaps: findGaps(view.entities, view.areas, analysis),
            leverage: leveragePoints(view.entities, analysis),
          }
        : null,
    [view, analysis, filters.minScore],
  );
  const matchIds = useMemo(() => {
    if (!model || !query.trim()) return null;
    return new Set(model.entities.filter((e) => matchesQuery(e, query)).map((e) => e.id));
  }, [model, query]);

  const layoutInput = useMemo(
    () =>
      view
        ? {
            areas: view.areas,
            entities: view.assessed,
            areaIds: view.areaIds,
            minScore: filters.minScore,
            unassessed: view.unassessed.map((entity) => ({ entity, weight: networkPercentile(analysis, entity.id) })),
          }
        : null,
    [view, analysis, filters.minScore],
  );
  const regionMeta = useMemo(() => {
    const meta: Record<string, RegionMeta> = {};
    bundle?.areas.forEach((s) => {
      meta[s.area.id] = { sub: s.scored ? `${s.concentration} · ${s.scored} scored` : 'No scores', shares: s.top.map((t) => t.share) };
    });
    if (view) meta[UNASSESSED_REGION] = { sub: `${view.unassessed.length} · placed by network position`, shares: [] };
    return meta;
  }, [bundle, view]);
  const ties = useMemo(() => {
    if (!analysis || !selectedId) return [] as string[];
    const g = analysis.graph;
    const i = g.index.get(selectedId);
    return i === undefined ? [] : g.nbrs[i].map((j) => g.ids[j]);
  }, [analysis, selectedId]);

  const selected = (selectedId && model?.byId.get(selectedId)) || null;
  const compareEntities = useMemo(
    () => compareIds.map((id) => model?.byId.get(id)).filter((e): e is InfluenceEntity => !!e),
    [compareIds, model],
  );
  const hoverId = panelHover || lensHover;
  const leveragePoint = useMemo(() => {
    if (!selected || !bundle) return null;
    return bundle.leverage.points.find((p) => p.entity.id === selected.id) || null;
  }, [selected, bundle]);

  // An id from a link that the data no longer has points at nothing, rather
  // than at a pane that never fills
  useEffect(() => {
    if (!model) return;
    const goneEntity = !!selectedId && !model.byId.has(selectedId);
    const goneArea = !!focusAreaId && !model.areas.some((area) => area.id === focusAreaId);
    if (goneEntity || goneArea) {
      setView({ ...(goneEntity ? { sel: null } : {}), ...(goneArea ? { area: null } : {}) });
    }
  }, [model, selectedId, focusAreaId, setView]);

  /* ---------------- Actions ---------------- */
  const lensRef = useRef(lens);
  lensRef.current = lens;
  const rightModeRef = useRef(rightMode);
  rightModeRef.current = rightMode;

  const select = useCallback(
    (id: string | null, opts: { fly?: boolean; keepPanel?: boolean } = {}) => {
      if (!id) {
        // Letting an entity go closes the dossier, not whatever else is open
        setView({ sel: null, ...(rightModeRef.current === 'dossier' ? { panel: null } : {}) });
        return;
      }
      setView({ sel: id, ...(isDesktop && !opts.keepPanel ? { panel: 'dossier' as const } : {}) });
      if (!isDesktop && !opts.keepPanel) setSheet('dossier');
      if (opts.fly && lensRef.current === 'map') mapRef.current?.focusEntity(id);
    },
    [isDesktop, setView],
  );

  const openProfile = useCallback(
    (id: string) => {
      const entity = model?.byId.get(id);
      if (entity) navigate(profilePath(entity));
    },
    [model, navigate],
  );

  const toggleCompare = useCallback(
    (id: string, opts: { reveal?: boolean } = {}) => {
      const adding = !compareIds.includes(id);
      const next = adding
        ? compareIds.length >= MAX_COMPARE
          ? compareIds
          : [...compareIds, id]
        : compareIds.filter((x) => x !== id);
      // A second actor is the point at which a comparison is worth showing
      const reveal = !!opts.reveal && adding && compareIds.length >= 1;
      setView({ compare: next, ...(reveal && isDesktop ? { panel: 'compare' as const } : {}) });
      if (reveal && !isDesktop) setSheet('compare');
    },
    [compareIds, isDesktop, setView],
  );

  const openAsk = useCallback(() => {
    if (isDesktop) {
      setView({ panel: 'ask' });
      window.setTimeout(() => askRef.current?.focus(), 80);
    } else {
      setSheet('ask');
    }
  }, [isDesktop, setView]);

  const focusArea = useCallback(
    (areaId: string) => {
      const toggledOff = focusAreaId === areaId;
      const area = toggledOff ? null : areaId;
      if (!isDesktop) setSheet(null);
      // The matrix answers an area by sorting on its column; the other lenses
      // are shown on the map
      if (lensRef.current === 'matrix') {
        setView({ area, sort: { key: areaId, dir: 'desc' } });
        return;
      }
      setView({ area, lens: 'map' });
      window.setTimeout(() => (toggledOff ? mapRef.current?.fit() : mapRef.current?.focusRegion(areaId)), lensRef.current === 'map' ? 0 : 80);
    },
    [focusAreaId, isDesktop, setView],
  );

  const showReferences = useCallback(
    (ids: string[]) => {
      setHighlightIds(ids);
      if (!isDesktop) setSheet(null);
      if (lensRef.current === 'map') mapRef.current?.focusEntities(ids);
    },
    [isDesktop],
  );

  const entityColor = useCallback((id: string) => {
    const e = model?.byId.get(id);
    return e ? areaColor(e.dominant?.color) : undefined;
  }, [model]);

  const buildContext = useCallback(() => {
    if (!bundle || !view || !model) return '';
    return buildInfluenceContext({
      bundle,
      entities: view.entities,
      totalEntities: model.entities.length,
      filters,
      areaLabels: view.areas.map((a) => a.label),
      lens: LENS_LABEL[lens],
      selected,
      compare: compareEntities,
      analysis,
    });
  }, [bundle, view, model, filters, lens, selected, compareEntities, analysis]);

  const suggestions = useMemo(() => (bundle ? influenceSuggestions(bundle, selected) : []), [bundle, selected]);
  const focusIds = useMemo(() => Array.from(new Set([selectedId, ...compareIds].filter((x): x is string => !!x))), [selectedId, compareIds]);

  /* ---------------- Keyboard ---------------- */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openAsk();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (event.key === '1' || event.key === '2' || event.key === '3') {
        setLens(LENSES[Number(event.key) - 1]);
      } else if (event.key === '/') {
        // This page's own search wins over the app-wide search shortcut
        event.preventDefault();
        event.stopPropagation();
        if (!isDesktop) setPhoneSearch(true);
        else if (searchRef.current) searchRef.current.focus();
        else setBarSearch(true);
      } else if (event.key === 'f' || event.key === 'F') {
        if (lensRef.current === 'map') mapRef.current?.fit();
      } else if (event.key === 'Escape') {
        if (filtersOpen) setFiltersOpen(false);
        else if (highlightIds.length) setHighlightIds([]);
        else if (selectedId) select(null);
        else if (rightMode) setView({ panel: null });
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [openAsk, setLens, isDesktop, filtersOpen, highlightIds.length, selectedId, rightMode, select, setView]);

  // Top bar density follows the workspace width (the app sidebar can take a lot of it)
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setRootWidth(el.clientWidth));
    ro.observe(el);
    setRootWidth(el.clientWidth);
    return () => ro.disconnect();
  }, [status]);

  // Close the filters popover on outside press
  useEffect(() => {
    if (!filtersOpen || !isDesktop) return;
    const onDown = (event: PointerEvent) => {
      if (!filtersRef.current?.contains(event.target as Node)) setFiltersOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [filtersOpen, isDesktop]);

  /* ---------------- Loading / error / empty ---------------- */
  if (status !== 'ready' || !model || !view || !bundle || !layoutInput) {
    return (
      <DashboardLayout>
        <div className={WORKSPACE_CLASS} style={WORKSPACE_STYLE}>
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            {status === 'loading' ? (
              <span className="inline-flex items-center gap-2 text-[13px] text-neutral-400">
                <Spinner /> Loading influence
              </span>
            ) : (
              <>
                <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error || 'No data available'}</p>
                <Button size="sm" variant="secondary" onClick={() => reload()}>
                  Retry
                </Button>
              </>
            )}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const assessedCount = view.assessed.length;
  const filterCount = activeFilterCount(filters);

  /* ---------------- Panels ---------------- */
  const briefPanel = (
    <BriefPanel
      domain="influence"
      buildContext={buildContext}
      focusIds={compareIds.length > 1 ? compareIds : undefined}
      onEntity={(id) => select(id, { fly: true })}
      onEntityHover={setPanelHover}
      entityColor={entityColor}
      onReferences={setHighlightIds}
      scopeLabel={compareIds.length > 1 ? `${compareIds.length} compared actors` : filterCount ? 'Filtered view' : 'Whole landscape'}
      scopeKey={`${JSON.stringify(filters)}|${compareIds.length > 1 ? compareIds.join(',') : ''}`}
    />
  );

  const rail = (
    <BriefingRail
      bundle={bundle}
      inScope={view.entities}
      totalEntities={model.entities.length}
      focusAreaId={focusAreaId}
      selectedId={selectedId}
      onFocusArea={focusArea}
      onSelect={(id) => select(id, { fly: true })}
      onHover={setPanelHover}
      assessment={briefPanel}
    />
  );

  const dossier = (titleId?: string) =>
    selected ? (
      <Dossier
        entity={selected}
        model={model}
        analysis={analysis}
        leverage={leveragePoint}
        compareIds={compareIds}
        assess={assess}
        reducedMotion={reducedMotion}
        titleId={titleId}
        hideClose={!isDesktop}
        onClose={() => (isDesktop ? select(null) : setSheet(null))}
        onSelect={(id) => select(id, { fly: true })}
        onHover={setPanelHover}
        onToggleCompare={(id) => toggleCompare(id, { reveal: true })}
        onOpenProfile={(e) => navigate(profilePath(e))}
        onEditScores={(e) => navigate(`${profilePath(e)}/edit`)}
        onAccepted={() => reload({ quiet: true })}
      />
    ) : (
      <EmptyState icon={UserRound} title="Select an entity" className="h-full" />
    );

  const compare = (titleId?: string) => (
    <ComparePanel
      entities={compareEntities}
      model={model}
      analysis={analysis}
      titleId={titleId}
      onRemove={(id) => toggleCompare(id)}
      onSelect={(id) => select(id, { fly: true })}
      onHover={setPanelHover}
      onBack={selected ? () => (isDesktop ? setView({ panel: 'dossier' }) : setSheet('dossier')) : undefined}
      onClose={() => {
        setView({ compare: NO_IDS, ...(isDesktop ? { panel: selected ? ('dossier' as const) : null } : {}) });
        if (!isDesktop) setSheet(selected ? 'dossier' : null);
      }}
    />
  );

  const ask = (
    <AskPanel
      ref={askRef}
      domain="influence"
      title="Ask about influence"
      placeholder="Ask about influence…"
      suggestions={suggestions}
      buildContext={buildContext}
      focusIds={focusIds}
      onEntity={(id) => select(id, { fly: true, keepPanel: isDesktop })}
      onEntityHover={setPanelHover}
      entityColor={entityColor}
      onReferences={setHighlightIds}
      onShowReferences={showReferences}
      showReferencesLabel={lens === 'map' ? 'Show on map' : 'Highlight'}
      scopeLabel={selected ? `Focused on ${selected.label}` : undefined}
      onClose={isDesktop ? () => setView({ panel: selected ? 'dossier' : null }) : undefined}
      autoFocus={isDesktop}
      className="h-full"
    />
  );

  const wideBar = rootWidth >= 1180;
  const iconLenses = isDesktop && rootWidth < 880;
  const lensItems = LENSES.map((l) => ({
    value: l,
    title: `${LENS_LABEL[l]} (${LENSES.indexOf(l) + 1})`,
    label: (
      <>
        {l === 'map' ? (
          <Orbit className="h-3.5 w-3.5" strokeWidth={1.75} />
        ) : l === 'matrix' ? (
          <Grid3x3 className="h-3.5 w-3.5" strokeWidth={1.75} />
        ) : (
          <ChartScatter className="h-3.5 w-3.5" strokeWidth={1.75} />
        )}
        {iconLenses ? <span className="sr-only">{LENS_LABEL[l]}</span> : l === 'map' && !isDesktop ? 'Map' : LENS_LABEL[l]}
      </>
    ),
  }));

  const moreItems = [
    { label: 'Refresh data', icon: RefreshCw, onSelect: () => reload({ quiet: true }) },
    ...(lens === 'map' ? [{ label: 'Fit map', icon: Scan, onSelect: () => mapRef.current?.fit() }] : []),
  ];

  const meta = (
    <p className="flex min-w-0 items-center gap-1.5 truncate text-[12px] tabular-nums text-neutral-500">
      <span className="truncate">
        {view.entities.length} entities · {view.areas.length} areas · {assessedCount} assessed
      </span>
      {model.limited && (
        <span title="This server has no intelligence module yet: network insight is limited. Update the server." className="flex-shrink-0">
          <Pill tone="outline">Limited data</Pill>
        </span>
      )}
      {refreshing && <Spinner className="h-3 w-3 flex-shrink-0 text-neutral-400" />}
    </p>
  );

  const noData = model.entities.length === 0;
  const noMatches = !noData && view.entities.length === 0;

  return (
    <DashboardLayout>
      <InfluenceStyles />
      <div ref={rootRef} className={WORKSPACE_CLASS} style={WORKSPACE_STYLE}>
        {/* ---------------- Top bar ---------------- */}
        {isDesktop ? (
          <header className="relative z-30 flex h-14 flex-shrink-0 items-center gap-3 border-b border-neutral-200/70 px-3">
            <IconButton
              label={railOpen ? 'Hide briefing' : 'Show briefing'}
              size="sm"
              onClick={() => setView({ rail: !railOpen })}
              aria-pressed={railOpen}
              className={railOpen ? '!text-ink' : ''}
            >
              <PanelLeft className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
            <div className="min-w-0 max-w-[260px] flex-shrink">
              <h1 className="truncate text-[17px] leading-tight tracking-[-0.02em] text-ink">Influence</h1>
              {meta}
            </div>
            <div className="flex min-w-0 flex-1 justify-center">
              <SegmentedControl label="Lens" value={lens} onChange={setLens} items={lensItems} className="[&>button]:!h-7 [&>button]:!px-3 [&>button]:!text-[12.5px]" />
            </div>
            {wideBar ? (
              <EntitySearch model={model} value={search} onChange={setSearch} onPick={(id) => select(id, { fly: true })} inputRef={searchRef} className="w-52" />
            ) : (
              <div className="relative">
                <IconButton label="Search (/)" size="sm" onClick={() => setBarSearch((v) => !v)} aria-pressed={barSearch} className={barSearch || search ? '!text-ink bg-neutral-100' : ''}>
                  <Search className="h-4 w-4" strokeWidth={1.75} />
                </IconButton>
                {barSearch && (
                  <div className={`absolute right-0 top-[calc(100%+8px)] z-40 w-[300px] rounded-2xl p-2 infl-rise ${FLOATING}`}>
                    <EntitySearch
                      model={model}
                      value={search}
                      onChange={setSearch}
                      onPick={(id) => select(id, { fly: true })}
                      inputRef={searchRef}
                      autoFocus
                      onDone={() => setBarSearch(false)}
                    />
                  </div>
                )}
              </div>
            )}
            <div ref={filtersRef} className="relative">
              <Button size="sm" variant={filterCount ? 'dark' : 'secondary'} onClick={() => setFiltersOpen((v) => !v)} aria-expanded={filtersOpen}>
                <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
                Filters
                {filterCount > 0 && <span className="tabular-nums opacity-70">{filterCount}</span>}
              </Button>
              {filtersOpen && (
                <div className={`absolute right-0 top-[calc(100%+8px)] z-40 w-[300px] rounded-2xl p-4 infl-rise ${FLOATING}`} role="dialog" aria-label="Filters">
                  <FiltersPanel model={model} filters={filters} onChange={setFilters} />
                </div>
              )}
            </div>
            <Button size="sm" variant={rightMode === 'ask' ? 'dark' : 'secondary'} onClick={() => (rightMode === 'ask' ? setView({ panel: selected ? 'dossier' : null }) : openAsk())} title="Ask (⌘K)">
              <Sparkles className={`h-3.5 w-3.5 ${rightMode === 'ask' ? '' : 'text-primary-600'}`} strokeWidth={1.75} />
              Ask
              {wideBar && <kbd className="ml-0.5 font-ui text-[11px] text-neutral-400">⌘K</kbd>}
            </Button>
            <Menu
              items={moreItems}
              trigger={(props) => (
                <IconButton label="More" size="sm" {...props}>
                  <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                </IconButton>
              )}
            />
          </header>
        ) : (
          <header className="relative z-30 flex-shrink-0 border-b border-neutral-200/70 px-3 pb-2 pt-2.5">
            <div className="flex items-center gap-1">
              <div className="min-w-0 flex-1 pl-1">
                <h1 className="truncate text-[17px] leading-tight tracking-[-0.02em] text-ink">Influence</h1>
                {meta}
              </div>
              <IconButton label="Search" size="sm" onClick={() => setPhoneSearch((v) => !v)} aria-pressed={phoneSearch} className={phoneSearch || search ? '!text-ink bg-neutral-100' : ''}>
                <Search className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
              <IconButton label="Filters" size="sm" onClick={() => setFiltersOpen(true)} className={filterCount ? '!text-ink bg-neutral-100' : ''}>
                <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
              <Menu
                items={moreItems}
                trigger={(props) => (
                  <IconButton label="More" size="sm" {...props}>
                    <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                  </IconButton>
                )}
              />
            </div>
            {phoneSearch && (
              <EntitySearch
                model={model}
                value={search}
                onChange={setSearch}
                onPick={(id) => select(id, { fly: true })}
                inputRef={searchRef}
                autoFocus
                onDone={() => setPhoneSearch(false)}
                className="mt-2 infl-rise"
              />
            )}
            <SegmentedControl label="Lens" value={lens} onChange={setLens} items={lensItems} className="mt-2 w-full [&>button]:flex-1 [&>button]:justify-center [&>button]:!px-2" />
          </header>
        )}

        {/* ---------------- Body ---------------- */}
        <div className="relative flex min-h-0 flex-1">
          {isDesktop && railOpen && (
            <aside aria-label="Briefing" className="w-[320px] flex-shrink-0 overflow-y-auto overscroll-contain border-r border-neutral-200/70 infl-slide-left">
              {rail}
            </aside>
          )}

          <main className="relative min-w-0 flex-1 overflow-hidden bg-white" aria-label={LENS_LABEL[lens]}>
            {noData ? (
              <EmptyState icon={ChartNoAxesColumn} title="No influence data yet" className="h-full" action={<Button size="sm" variant="secondary" onClick={() => navigate('/stakeholders')}>Add scores</Button>} />
            ) : (
              LENSES.filter((l) => visited.includes(l)).map((l) => {
                const on = lens === l;
                return (
                  <div
                    key={l}
                    aria-hidden={!on}
                    className={`absolute inset-0 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
                      on ? 'opacity-100' : 'pointer-events-none scale-[0.985] opacity-0'
                    }`}
                    style={{ visibility: on ? 'visible' : 'hidden', transitionProperty: 'opacity, transform, visibility', transitionDelay: on ? '0ms, 0ms, 0ms' : '0ms, 0ms, 300ms' }}
                  >
                    {l === 'map' && (
                      <PowerMap
                        ref={mapRef}
                        input={layoutInput}
                        entityById={model.byId}
                        regionMeta={regionMeta}
                        selectedId={selectedId}
                        hoverId={hoverId}
                        compareIds={compareIds}
                        highlightIds={highlightIds}
                        matchIds={matchIds}
                        ties={ties}
                        active={on}
                        reducedMotion={reducedMotion}
                        dockSpace={!isDesktop}
                        onHover={setLensHover}
                        onSelect={(id) => select(id)}
                        onOpen={openProfile}
                      />
                    )}
                    {l === 'matrix' && (
                      <MatrixLens
                        entities={view.entities}
                        areas={view.areas}
                        summaries={bundle.areas}
                        analysis={analysis}
                        sort={matrixSort}
                        onSort={setMatrixSort}
                        query={query}
                        matchIds={matchIds}
                        selectedId={selectedId}
                        hoverId={hoverId}
                        compareIds={compareIds}
                        highlightIds={highlightIds}
                        active={on}
                        compact={!isDesktop}
                        onHover={setLensHover}
                        onSelect={(id) => select(id)}
                        onOpen={openProfile}
                      />
                    )}
                    {l === 'leverage' && (
                      <LeverageLens
                        points={bundle.leverage.points}
                        unassessed={bundle.leverage.unassessed}
                        counts={bundle.leverage.counts}
                        selectedId={selectedId}
                        hoverId={hoverId}
                        compareIds={compareIds}
                        highlightIds={highlightIds}
                        matchIds={matchIds}
                        active={on}
                        dockSpace={!isDesktop}
                        onHover={setLensHover}
                        onSelect={(id) => select(id)}
                        onOpen={openProfile}
                      />
                    )}
                  </div>
                );
              })
            )}

            {noMatches && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                <div className={`pointer-events-auto rounded-2xl ${FLOATING}`}>
                  <EmptyState title="Nothing matches these filters" action={<Button size="sm" variant="secondary" onClick={() => setFilters(DEFAULT_FILTERS)}>Reset filters</Button>} />
                </div>
              </div>
            )}

            {/* Floating status: AI highlights and compare tray */}
            {(highlightIds.length > 0 || (compareIds.length > 0 && rightMode !== 'compare' && sheet !== 'compare')) && (
              <div className={`pointer-events-none absolute inset-x-3 z-20 flex flex-col items-center gap-2 ${isDesktop ? 'bottom-3' : 'bottom-[68px]'}`}>
                {highlightIds.length > 0 && (
                  <div role="status" className={`pointer-events-auto flex max-w-full items-center gap-2 rounded-full py-1 pl-3 pr-1 infl-rise ${FLOATING}`}>
                    <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-600 infl-pulse" />
                    <span className="truncate text-[12.5px] text-ink">
                      {highlightIds.length} referenced by AI
                    </span>
                    {lens === 'map' && (
                      <Button size="xs" variant="ghost" onClick={() => mapRef.current?.focusEntities(highlightIds)}>
                        Show
                      </Button>
                    )}
                    <IconButton label="Clear highlights" size="xs" onClick={() => setHighlightIds([])}>
                      <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </IconButton>
                  </div>
                )}
                {compareIds.length > 0 && rightMode !== 'compare' && sheet !== 'compare' && (
                  <div role="status" className={`pointer-events-auto flex max-w-full items-center gap-2 rounded-full py-1 pl-3.5 pr-1 infl-rise ${FLOATING}`}>
                    <span className="truncate text-[12.5px] text-ink">
                      Comparing <span className="tabular-nums">{compareIds.length}</span> of {MAX_COMPARE}
                    </span>
                    <Button size="xs" variant="dark" onClick={() => (isDesktop ? setView({ panel: 'compare' }) : setSheet('compare'))}>
                      View
                    </Button>
                    <IconButton label="Clear compare" size="xs" onClick={() => setView({ compare: NO_IDS })}>
                      <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </IconButton>
                  </div>
                )}
              </div>
            )}

            {/* Phone dock */}
            {!isDesktop && !noData && (
              <nav aria-label="Panels" className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center">
                <div className={`pointer-events-auto flex items-center gap-0.5 rounded-full p-1 ${FLOATING}`}>
                  {(
                    [
                      { tab: 'briefing', label: 'Briefing', icon: FileText, disabled: false },
                      { tab: 'dossier', label: 'Dossier', icon: UserRound, disabled: !selected },
                      { tab: 'ask', label: 'Ask', icon: Sparkles, disabled: false },
                    ] as const
                  ).map(({ tab, label, icon: Icon, disabled }) => (
                    <button
                      key={tab}
                      type="button"
                      disabled={disabled}
                      onClick={() => setSheet(tab)}
                      className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13px] text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-ink disabled:opacity-40"
                    >
                      <Icon className={`h-4 w-4 ${tab === 'ask' ? 'text-primary-600' : ''}`} strokeWidth={1.75} />
                      {label}
                    </button>
                  ))}
                </div>
              </nav>
            )}
          </main>

          {isDesktop && rightMode && (
            <aside
              key={rightMode}
              aria-label={rightMode === 'ask' ? 'Ask' : rightMode === 'compare' ? 'Compare' : 'Dossier'}
              className="flex w-[360px] flex-shrink-0 flex-col border-l border-neutral-200/70 bg-white infl-slide-right"
            >
              {rightMode === 'dossier' && dossier()}
              {rightMode === 'compare' && compare()}
              {rightMode === 'ask' && ask}
            </aside>
          )}
        </div>
      </div>

      {/* ---------------- Phone sheets ---------------- */}
      <Dialog open={!isDesktop && !!sheet} onClose={() => setSheet(null)} labelledBy={sheetTitleId} handle className="h-[85dvh] sm:max-w-lg">
        <div className="flex flex-shrink-0 items-center gap-2 px-4 pb-2 pt-1 sm:pt-4">
          <h2 id={sheetTitleId} className="sr-only">
            {sheet === 'ask' ? 'Ask' : sheet === 'briefing' ? 'Briefing' : sheet === 'compare' ? 'Compare' : 'Dossier'}
          </h2>
          <SegmentedControl
            label="Panel"
            value={sheet === 'compare' ? 'dossier' : sheet || 'briefing'}
            onChange={(tab) => setSheet(tab)}
            className="flex-1 [&>button]:flex-1 [&>button]:justify-center"
            items={[
              { value: 'briefing' as SheetTab, label: 'Briefing' },
              { value: 'dossier' as SheetTab, label: 'Dossier', disabled: !selected },
              { value: 'ask' as SheetTab, label: 'Ask' },
            ]}
          />
          <IconButton label="Close" size="sm" onClick={() => setSheet(null)}>
            <X className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {sheet === 'briefing' && <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{rail}</div>}
          {sheet === 'dossier' && dossier()}
          {sheet === 'compare' && compare()}
          {sheet === 'ask' && ask}
        </div>
      </Dialog>

      <Dialog open={!isDesktop && filtersOpen} onClose={() => setFiltersOpen(false)} label="Filters" handle className="max-h-[85dvh] sm:max-w-sm">
        <DialogHeader title="Filters" onClose={() => setFiltersOpen(false)} />
        <DialogBody>
          <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <FiltersPanel model={model} filters={filters} onChange={setFilters} />
          </div>
        </DialogBody>
      </Dialog>
    </DashboardLayout>
  );
};

export default InfluenceWorkspace;
